import type {
  AccommodationAvailableRoomsInput,
  AccommodationRoomFacilityType,
} from '@data-access/graphql/schema.types'
import type {
  AccommodationAvailableRoomsQuery,
  AccommodationAvailableRoomsQueryVariables,
  AccommodationSearchQuery,
  AccommodationSearchQueryVariables,
} from '@data-access/lib/gql/accommodations/documents'
import { getLowestRate } from '@feat/feat-accommodation/availability'
import type { UrlState } from '@feat/feat-url-state/schema'
import { formatIsoDate } from '@utils/utils-format'

import { isNotNil } from 'es-toolkit'
import type { LodgingStay } from 'src/lib/hotel-ads/microdata'
import { mapAccommodationPaxInput } from './map-accommodation-pax-input'

export const ACCOMMODATION_DETAIL_STALE_TIME = 15_000

export type AccommodationDetailQueryInput = {
  accommodationId: number
  locale: string
  urlState: UrlState
}

export type AvailableRoom = Required<ReturnType<typeof selectAvailableRooms>[number]>

export function getLodgingStay({ from, to, adults, children }: UrlState): LodgingStay | null {
  if (!from || !to) return null

  return {
    checkin: formatIsoDate(from),
    checkout: formatIsoDate(to),
    adults,
    childCount: children?.length ?? 0,
  }
}

export function getAccommodationSearchVariables({
  accommodationId,
  locale,
  urlState,
}: AccommodationDetailQueryInput): AccommodationSearchQueryVariables {
  return {
    filter: {
      ids: [accommodationId],
      durationOfStay:
        urlState.from && urlState.to
          ? {
              checkin: formatIsoDate(urlState.from),
              checkout: formatIsoDate(urlState.to),
            }
          : undefined,
      allocation: mapAccommodationPaxInput(urlState),
    },
    locale,
    pagination: { skip: 0, take: 1 },
  }
}

export function getAccommodationAvailableRoomsInput({
  accommodationId,
  urlState,
}: Pick<
  AccommodationDetailQueryInput,
  'accommodationId' | 'urlState'
>): AccommodationAvailableRoomsInput | null {
  if (!urlState.from || !urlState.to) {
    return null
  }

  return {
    id: accommodationId,
    checkin: formatIsoDate(urlState.from),
    checkout: formatIsoDate(urlState.to),
    allocation: mapAccommodationPaxInput(urlState),
  }
}

export function getAccommodationAvailableRoomsVariables(
  input: Pick<AccommodationDetailQueryInput, 'accommodationId' | 'urlState'>
): AccommodationAvailableRoomsQueryVariables | null {
  const availableRoomsInput = getAccommodationAvailableRoomsInput(input)

  if (!availableRoomsInput) {
    return null
  }

  return { input: availableRoomsInput }
}

export function selectAccommodationSearchResult(data: AccommodationSearchQuery) {
  return data.accommodationSearch
}

export function selectAvailableRooms({ availableRooms }: AccommodationAvailableRoomsQuery) {
  return availableRooms.rooms.map(room => ({
    ...room,
    facilityHighlights: filterFacilityHighlights(room.facilityTypes),
    lowestRate: getLowestRate(room.rates).pricing,
    images: room.images.map(image => ({
      url: image.url,
      alt: room.title,
    })),
  }))
}

function filterFacilityHighlights(facilityTypes: AccommodationRoomFacilityType[]) {
  return facilityTypes
    .filter(facilityType => facilityType.id === 8)
    .flatMap(facilityType => facilityType.facilities)
    .filter(isNotNil)
}
